/**
 * @fileoverview Cross-container drag coordination between table headers and the ColumnShelf.
 * Chips (hidden columns) drag INTO the header row to insert; headers drag INTO the shelf to hide.
 * @module shared/hooks/useColumnShelfDnd
 */

import { useCallback, useState } from 'react'
import type { DragEndEvent, DragMoveEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'

/** Droppable id of the shelf container. */
export const SHELF_DROPPABLE_ID = 'column-shelf'

/**
 * Droppable id of the placeholder header cell shown when no metric column is
 * visible — without it a chip drag has zero valid targets (headers are the
 * only insert droppables, and none render in that state). Dropping a chip on
 * it appends the column at the end of the order.
 */
export const EMPTY_HEADER_DROP_ID = 'column-shelf-empty-target'

/**
 * Droppable id of the shelf toggle button. Dropping a header on it hides the
 * column — the always-visible remove target while the shelf itself is closed.
 */
export const SHELF_TOGGLE_DROP_ID = 'column-shelf-toggle'

const CHIP_PREFIX = 'chip:'

/**
 * Build the dnd id for a shelf chip. Prefixed so chip ids never collide with
 * header sortable ids (which are bare column ids).
 */
export function chipDndId(columnId: string): string {
  return `${CHIP_PREFIX}${columnId}`
}

/** Extract the column id from a chip dnd id, or null if the id is not a chip's. */
export function parseChipId(dndId: string): string | null {
  return dndId.startsWith(CHIP_PREFIX) ? dndId.slice(CHIP_PREFIX.length) : null
}

/** Outcome of a drag-end gesture. */
export type ShelfDragAction<TColumnId extends string> =
  | { type: 'insert'; columnId: TColumnId; index: number }
  | { type: 'hide'; columnId: TColumnId }
  | { type: 'reorder' }
  | { type: 'none' }

export interface ResolveDragEndParams<TColumnId extends string> {
  /** dnd id of the dragged item (chip-prefixed or bare header id). */
  activeId: string
  /** dnd id under the pointer at drop time, or null. */
  overId: string | null
  /** Full column order (all columns, visible or not). */
  columnOrder: readonly TColumnId[]
  /** Horizontal center of the dragged item; null → insert before target. */
  activeCenterX: number | null
  /** Horizontal center of the hovered target; null → insert before target. */
  overCenterX: number | null
}

/**
 * Pure decision function for drag-end. Insert index is relative to the
 * CURRENT columnOrder ("insert before the column at index"), +1 when the
 * dragged center is right of the target center.
 */
export function resolveDragEnd<TColumnId extends string>(
  params: ResolveDragEndParams<TColumnId>
): ShelfDragAction<TColumnId> {
  const { activeId, overId, columnOrder, activeCenterX, overCenterX } = params
  const chipColumn = parseChipId(activeId) as TColumnId | null

  if (chipColumn !== null) {
    if (overId === null || overId === SHELF_DROPPABLE_ID) return { type: 'none' }
    // Validate: the chip's column must itself be a known column
    if (!columnOrder.includes(chipColumn)) return { type: 'none' }
    // Empty-table placeholder: append at the end of the order
    if (overId === EMPTY_HEADER_DROP_ID) {
      return { type: 'insert', columnId: chipColumn, index: columnOrder.length }
    }
    const targetIndex = columnOrder.indexOf(overId as TColumnId)
    if (targetIndex === -1) return { type: 'none' }
    const after = activeCenterX !== null && overCenterX !== null && activeCenterX > overCenterX
    return { type: 'insert', columnId: chipColumn, index: targetIndex + (after ? 1 : 0) }
  }

  if (overId === SHELF_DROPPABLE_ID || overId === SHELF_TOGGLE_DROP_ID) {
    // Validate: only known columns can be hidden
    if (!columnOrder.includes(activeId as TColumnId)) return { type: 'none' }
    return { type: 'hide', columnId: activeId as TColumnId }
  }
  if (
    overId !== null &&
    columnOrder.includes(activeId as TColumnId) &&
    columnOrder.includes(overId as TColumnId)
  ) {
    return { type: 'reorder' }
  }
  return { type: 'none' }
}

interface RectLike {
  left: number
  width: number
}

function rectCenterX(rect: RectLike | null | undefined): number | null {
  return rect != null ? rect.left + rect.width / 2 : null
}

/** Insertion indicator shown on a header while a chip hovers it. */
export interface ShelfDropIndicator<TColumnId extends string> {
  columnId: TColumnId
  edge: 'left' | 'right'
}

export interface UseColumnShelfDndOptions<TColumnId extends string> {
  /** Full column order (all columns, visible or not). */
  columnOrder: readonly TColumnId[]
  /** Insert a column at an order index and make it visible (evicting at cap). */
  showColumnAt: (columnId: TColumnId, index: number) => void
  /** Hide a visible column. */
  hideColumn: (columnId: TColumnId) => void
  /** Existing header-reorder handler (useTableColumnState.handleDragEnd). */
  reorder: (event: DragEndEvent) => void
}

export interface UseColumnShelfDndReturn<TColumnId extends string> {
  /** Column id of the chip being dragged, or null (drives the DragOverlay). */
  activeChipColumn: TColumnId | null
  /**
   * Column id of the header being dragged, or null. Drives the header ghost
   * in the DragOverlay and the shelf's "drop to hide" hint.
   */
  activeHeaderColumn: TColumnId | null
  /** True while a header drag hovers the shelf drop zone. */
  isShelfDropTarget: boolean
  /** Header + edge to highlight while a chip hovers the header row. */
  dropIndicator: ShelfDropIndicator<TColumnId> | null
  onDragStart: (event: DragStartEvent) => void
  onDragMove: (event: DragMoveEvent) => void
  onDragOver: (event: DragOverEvent) => void
  onDragEnd: (event: DragEndEvent) => void
  onDragCancel: () => void
}

/**
 * Compute the chip-over-header indicator from active/over rects.
 * Returns null when the drag is not a chip-over-header scenario.
 */
function computeChipOverHeaderIndicator<TColumnId extends string>(
  activeId: string,
  overId: string | null,
  columnOrder: readonly TColumnId[],
  translatedRect: RectLike | null | undefined,
  overRect: RectLike | null | undefined
): ShelfDropIndicator<TColumnId> | null {
  const chipColumn = parseChipId(activeId)
  if (chipColumn === null) return null
  if (
    overId === null ||
    overId === SHELF_DROPPABLE_ID ||
    !columnOrder.includes(overId as TColumnId)
  ) {
    return null
  }
  const activeCenterX = rectCenterX(translatedRect)
  const overCenterX = rectCenterX(overRect)
  const after = activeCenterX !== null && overCenterX !== null && activeCenterX > overCenterX
  return { columnId: overId as TColumnId, edge: after ? 'right' : 'left' }
}

/**
 * Coordinates chip↔header cross-container drag for the ColumnShelf.
 *
 * @example
 * ```tsx
 * const dnd = useColumnShelfDnd({ columnOrder, showColumnAt, hideColumn: toggleColumn, reorder: handleDragEnd })
 * <DndContext onDragStart={dnd.onDragStart} onDragMove={dnd.onDragMove}
 *             onDragOver={dnd.onDragOver} onDragEnd={dnd.onDragEnd}
 *             onDragCancel={dnd.onDragCancel}>
 * ```
 */
export function useColumnShelfDnd<TColumnId extends string>({
  columnOrder,
  showColumnAt,
  hideColumn,
  reorder,
}: UseColumnShelfDndOptions<TColumnId>): UseColumnShelfDndReturn<TColumnId> {
  const [activeChipColumn, setActiveChipColumn] = useState<TColumnId | null>(null)
  const [activeHeaderColumn, setActiveHeaderColumn] = useState<TColumnId | null>(null)
  const [isShelfDropTarget, setIsShelfDropTarget] = useState(false)
  const [dropIndicator, setDropIndicator] = useState<ShelfDropIndicator<TColumnId> | null>(null)

  const reset = useCallback((): void => {
    setActiveChipColumn(null)
    setActiveHeaderColumn(null)
    setIsShelfDropTarget(false)
    setDropIndicator(null)
  }, [])

  const onDragStart = useCallback(
    (event: DragStartEvent): void => {
      // Reset any stale state from a prior drag before setting the new active item.
      reset()
      const activeId = String(event.active.id)
      const chipColumn = parseChipId(activeId) as TColumnId | null
      setActiveChipColumn(chipColumn)
      if (chipColumn === null && columnOrder.includes(activeId as TColumnId)) {
        setActiveHeaderColumn(activeId as TColumnId)
      }
    },
    [reset, columnOrder]
  )

  const onDragMove = useCallback(
    (event: DragMoveEvent): void => {
      const overId = event.over === null ? null : String(event.over.id)
      const activeId = String(event.active.id)
      const indicator = computeChipOverHeaderIndicator(
        activeId,
        overId,
        columnOrder,
        event.active.rect.current.translated,
        event.over?.rect
      )
      // Guard: only set state when something actually changed (fires per pointer move).
      setDropIndicator((prev) => {
        if (
          prev === null && indicator === null
        ) return prev
        if (
          prev !== null &&
          indicator !== null &&
          prev.columnId === indicator.columnId &&
          prev.edge === indicator.edge
        ) return prev
        return indicator
      })
    },
    [columnOrder]
  )

  const onDragOver = useCallback(
    (event: DragOverEvent): void => {
      const overId = event.over === null ? null : String(event.over.id)
      const activeId = String(event.active.id)
      const chipColumn = parseChipId(activeId)

      if (chipColumn !== null) {
        const indicator = computeChipOverHeaderIndicator(
          activeId,
          overId,
          columnOrder,
          event.active.rect.current.translated,
          event.over?.rect
        )
        setDropIndicator(indicator)
        return
      }
      setIsShelfDropTarget(overId === SHELF_DROPPABLE_ID)
    },
    [columnOrder]
  )

  const onDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const action = resolveDragEnd<TColumnId>({
        activeId: String(event.active.id),
        overId: event.over === null ? null : String(event.over.id),
        columnOrder,
        activeCenterX: rectCenterX(event.active.rect.current.translated),
        overCenterX: rectCenterX(event.over?.rect),
      })
      if (action.type === 'insert') showColumnAt(action.columnId, action.index)
      else if (action.type === 'hide') hideColumn(action.columnId)
      else if (action.type === 'reorder') reorder(event)
      reset()
    },
    [columnOrder, showColumnAt, hideColumn, reorder, reset]
  )

  return {
    activeChipColumn,
    activeHeaderColumn,
    isShelfDropTarget,
    dropIndicator,
    onDragStart,
    onDragMove,
    onDragOver,
    onDragEnd,
    onDragCancel: reset,
  }
}
